import { SetStateAction, FC, useContext, useEffect, useState, JSX } from "react";
import { toast } from "react-toastify";

import ProjectService from "@app/projects/projectService";
import { ProjectionProvider } from "@app/projects/results/projection-provider";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import { AccordionContainer, Accordion, formatDateTime, LoadingSpinner } from "@app/shared";
import { QESAlert, QESButton } from "@app/shared/qes-standards";
import { Roles, UserContext, UserInfo } from "@app/user";
import { CountyProjectContext } from "..";
import { ColumnConfig, Waffles } from "../../shared/waffles";
import { ProjectionProviderType } from "../enumerators";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import NetNewServicingProviderService from "./NetNewServicingProviderService";
import { NetNewProvider } from "../results/servicing-provider/NetNewProvider";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ZipProjectContext } from "../zip-projects/ZipProjectContext";

interface PendingProviderChangesProps {
  getTargets: boolean;
  getTerminations: boolean;
  projectId: number;
  processProviderChanges: (reprocessFailure: boolean) => Promise<void>;
  resetStatusBanners: (hasUnprocessedTerminations: boolean, hasUnprocessedTargets: boolean) => void;
  isZipProject: boolean;
}

interface PendingWaffleToppings {
  key: string;
  npi: string;
  tin: string;
  name: string;
  address: JSX.Element;
  specialtyCode: JSX.Element;
  reason: JSX.Element;
  action: string;
  actionBy: string;
  actionDate: string;
}

enum FlyoutMode {
  Edit = "edit",
  Process = "process",
}

const userMdIcon = <QesIcon isDecorative iconName={QesIcons.FontAwesome.UserMd} width="11" height="14" classNames="align-baseline mr-1" svgStyle={{ position: "relative", top: "1px" }} />;

const PendingProviderChanges: FC<React.PropsWithChildren<PendingProviderChangesProps>> = (props: PendingProviderChangesProps) => {
  // unprocessedTargets includes unprocessed excludes, too
  const [unprocessedTargets, setUnprocessedTargets] = useState<ProjectionProvider[]>();
  const [unprocessedNetNewProviders, setUnprocessedNetNewProviders] = useState<NetNewProvider[]>();
  const [unprocessedTerminations, setUnprocessedTerminations] = useState<NetworkServicingProviderTerminationModel[]>();
  const [projectService] = useState<ProjectService>(new ProjectService());
  const [netNewServicingProviderService] = useState<NetNewServicingProviderService>(new NetNewServicingProviderService());
  const [providerTerminationService] = useState<ProviderTerminationService>(new ProviderTerminationService());
  const [error, setError] = useState<JSX.Element>(null);
  const [selectedTerminations, setSelectedTerminations] = useState<string[]>([]);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [selectedExcludes, setSelectedExcludes] = useState<string[]>([]);
  const [selectedNetNewProviders, setSelectedNetNewProviders] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [mode, setMode] = useState<FlyoutMode>(FlyoutMode.Process);
  const [loading, setLoading] = useState(false);
  const user = useContext(UserContext);
  const projectContext = props.isZipProject ? useContext(ZipProjectContext).state : useContext(CountyProjectContext);
  const flyoutContext = useContext(FlyoutContext);


  useEffect(() => {
    if (props.getTargets) {
      setLoading(true);
      projectService.getUnprocessedProjectionProvider(props.projectId)
        .then((result) => {
          setUnprocessedTargets(result);
        }, () => {
          setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
        });

      setLoading(false);
    } else {
      setUnprocessedTargets([]);
    }
  }, [refreshKey]);


  useEffect(() => {
    if (props.getTargets && user.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {
      setLoading(true);
      netNewServicingProviderService.getUnprocessedNetNewProviders(props.projectId)
        .then((result) => {
          setUnprocessedNetNewProviders(result);
        }, () => {
          setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
        });

      setLoading(false);
    } else {
      setUnprocessedNetNewProviders([]);
    }
  }, [refreshKey]);

  useEffect(() => {
    if (props.getTerminations) {
      setLoading(true);
      providerTerminationService.getUnprocessedProviderTerminations(props.projectId)
        .then((result) => {
          setUnprocessedTerminations(result);
        }, () => {
          setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
        });
      setLoading(false);
    } else {
      setUnprocessedTerminations([]);
    }
  }, [refreshKey]);

  useEffect(() => {
    if (refreshKey > 0) {
      toast(selectedExcludes.length + selectedTargets.length + selectedTerminations.length + selectedNetNewProviders.length > 1 ? "Providers successfully removed" : "Provider successfully removed", { containerId: "z-index-9999" });
      setSelectedExcludes([]);
      setSelectedTargets([]);
      setSelectedNetNewProviders([]);
      setSelectedTerminations([]);
    }
  }, [refreshKey]);

  useEffect(() => {
    if (refreshKey > 0) {
      projectContext.setShouldRefreshProspectProviders(true);
      projectContext.setShouldRefreshMarketProviders(true);
      projectContext.setShouldRefreshNetworkProviders(true);
      projectContext.setShouldRefreshNetNewProviders(true);
    }
  }, [refreshKey]);

  useEffect(() => {
    if (refreshKey > 0 && unprocessedTerminations.length === 0 && unprocessedTargets.length === 0 && unprocessedNetNewProviders.length === 0) {
      flyoutContext.handleCloseFlyout().then(() => {
        projectContext.setProjectProjectionFlags(false, false); //TODO
      });
    }
  }, [refreshKey, unprocessedTargets, unprocessedTerminations, unprocessedNetNewProviders]);

  useEffect(() => {
    if ((unprocessedTerminations?.length == 0 || unprocessedTargets?.length == 0) && !flyoutContext.isFlyoutVisible) {
      props.resetStatusBanners(!(unprocessedTerminations?.length == 0), !(unprocessedTargets?.length == 0));
    }
  }, [flyoutContext.isFlyoutVisible])

  return (
    <>
      {error ? error :
        !!unprocessedTargets && !!unprocessedNetNewProviders && !!unprocessedTerminations && !loading ?
          renderLoadedContent(
            props,
            unprocessedTargets,
            unprocessedNetNewProviders,
            unprocessedTerminations,
            selectedTargets,
            setSelectedTargets,
            selectedExcludes,
            setSelectedExcludes,
            selectedNetNewProviders,
            setSelectedNetNewProviders,
            selectedTerminations,
            setSelectedTerminations,
            netNewServicingProviderService,
            projectService,
            providerTerminationService,
            refreshKey,
            setRefreshKey,
            user,
            mode,
            setMode,
          ) : <LoadingSpinner />}
    </>
  );
};

const renderLoadedContent = (
  props: PendingProviderChangesProps,
  unprocessedProjectedProviders: ProjectionProvider[],
  unprocessedNetNewProviders: NetNewProvider[],
  unprocessedTerminations: NetworkServicingProviderTerminationModel[],
  selectedTargets: string[],
  setSelectedTargets: React.Dispatch<SetStateAction<string[]>>,
  selectedExcludes: string[],
  setSelectedExcludes: React.Dispatch<SetStateAction<string[]>>,
  selectedNetNewProviders: string[],
  setSelectedNetNewProviders: React.Dispatch<SetStateAction<string[]>>,
  selectedTerminations: string[],
  setSelectedTerminations: React.Dispatch<SetStateAction<string[]>>,
  netNewServicingProviderService: NetNewServicingProviderService,
  projectService: ProjectService,
  providerTerminationService: ProviderTerminationService,
  refreshKey: number,
  setRefreshKey: React.Dispatch<SetStateAction<number>>,
  user: UserInfo,
  mode: FlyoutMode,
  setMode: React.Dispatch<SetStateAction<FlyoutMode>>,
) => {
  if (!unprocessedProjectedProviders.length && !unprocessedTerminations.length && !unprocessedNetNewProviders.length) {
    return renderNoResults();
  }

  const unprocessedTargets = unprocessedProjectedProviders.filter((upp) => upp.projectionType === ProjectionProviderType.Target);
  const unprocessedExcludes = unprocessedProjectedProviders.filter((upp) => upp.projectionType === ProjectionProviderType.ExcludeImpact);

  const removeSelectedProviders = () => {
    removeSelectedProjectionProviders(props.projectId, projectService, selectedTargets, unprocessedTargets, refreshKey, setRefreshKey);
    removeSelectedProjectionProviders(props.projectId, projectService, selectedExcludes, unprocessedExcludes, refreshKey, setRefreshKey);
    removeSelectedNetNewProviders(props.projectId, netNewServicingProviderService, selectedNetNewProviders, unprocessedNetNewProviders, refreshKey, setRefreshKey);
    removeSelectedTerminationProviders(props.projectId, unprocessedTerminations, providerTerminationService, selectedTerminations, refreshKey, setRefreshKey);
    setMode(FlyoutMode.Process);
  };

  const isEditMode = mode === FlyoutMode.Edit;

  const rightSideButtons = (
    !isEditMode ? (
      <div>
        <QESButton qesType="secondary" className="mr-3" onClick={() => setMode(FlyoutMode.Edit)} qesTestId="pending-providers__edit">Edit</QESButton>
        <QESButton qesType="primary" qesTestId="pending-providers__process" onClick={() => props.processProviderChanges(false)}>Process Listed Providers</QESButton>
      </div>
    ) : (
      <div>
        <QESButton qesType="secondary" className="mr-3" qesTestId="pending-providers__cancel-edit" onClick={() => setMode(FlyoutMode.Process)}>Stop Editing</QESButton>
        <QESButton qesType="primary-danger" qesTestId="pending-providers__remove" onClick={removeSelectedProviders} disabled={selectedExcludes.length + selectedTargets.length + selectedTerminations.length + selectedNetNewProviders.length === 0}>Remove Selected Providers</QESButton>
      </div>
    )
  );

  return (
    <>
      {isEditMode && <p>Select the providers you would like to remove from this list.</p>}
      {unprocessedTargets.length > 0 && renderProjectedProviders(unprocessedTargets, ProjectionProviderType.Target, selectedTargets, setSelectedTargets, user, isEditMode)}
      {unprocessedExcludes.length > 0 && renderProjectedProviders(unprocessedExcludes, ProjectionProviderType.ExcludeImpact, selectedExcludes, setSelectedExcludes, user, isEditMode)}
      {unprocessedNetNewProviders.length > 0 && renderNetNewProviders(unprocessedNetNewProviders, selectedNetNewProviders, setSelectedNetNewProviders, user, isEditMode)}
      {unprocessedTerminations.length > 0 && renderTerminatedProviders(unprocessedTerminations, selectedTerminations, setSelectedTerminations, user, isEditMode)}
      <FlyoutFooter
        submitButton={rightSideButtons}
      />
    </>
  );
};

const getColumnConfiguration = (user: UserInfo) => {
  const columnConfig: ColumnConfig = {
    action: {
      displayText: "Action",
      noSort: true,
      testId: "action",
    },
    actionBy: {
      displayText: "Action By",
      noSort: true,
      testId: "action-by",
    },
    actionDate: {
      displayText: "Action Date",
      noSort: true,
      testId: "action-date",
    },
    address: {
      displayText: "Address",
      hidden: false,
      noSort: true,
      testId: "address",
    },
    name: {
      displayText: "Name",
      hidden: false,
      noSort: true,
      testId: "name",
    },
    npi: {
      displayText: "NPI",
      hidden: false,
      minWidth: "85px",
      noSort: true,
      testId: "npi",
    },
    reason: {
      displayText: "Reason",
      noSort: true,
      testId: "reason",
    },
    specialtyCode: {
      displayText: "Specialty Code",
      hidden: false,
      noSort: true,
      testId: "specialtyCode",
    },
    tin: {
      displayText: "TIN",
      hidden: !user.roles.includes(Roles.TIN_VIEWER),
      noSort: true,
      testId: "tin",
    },
  };
  return columnConfig;
};

const renderProjectedProviders = (unprocessedProjectedProviders: ProjectionProvider[], projectionProviderType: ProjectionProviderType, selectedProviders: string[], setSelections: React.Dispatch<SetStateAction<string[]>>, user: UserInfo, isEditMode: boolean) => {
  const showNpiColumn = unprocessedProjectedProviders.some((upp) => !!upp.npi);
  const showTinColumn = unprocessedProjectedProviders.some((upp) => !!upp.tin);

  const updateSelectedProviders = (selectedIds: string[]) => {
    setSelections(selectedIds);
  };

  const columnConfig = getColumnConfiguration(user);
  columnConfig.npi.hidden = !showNpiColumn && showTinColumn;
  columnConfig.tin.hidden = columnConfig.tin.hidden || !showTinColumn;

  /* eslint-disable sort-keys */
  const data: PendingWaffleToppings[] = unprocessedProjectedProviders.map((upp) => (
    {
      key: upp.providerId.toString(),
      npi: upp.npi,
      tin: upp.tin,
      name: upp.displayName,
      address: (() => {
        if (upp.allLocations) {
          return (<div><i className={`suppress-panel-styles fa fa-check`} />All Locations</div>);
        } else {
          return (<>
            {upp.address ? <>{upp.address}<br /></> : null}
            {upp.address2 ? <>{upp.address2}<br /></> : null}
            {upp.city}
            {upp.city && (upp.state || upp.zip) ? ", " : null}
            {upp.state} {upp.zip}
          </>);
        }
      })(),
      specialtyCode: (() => {
        if (upp.allSpecialties) {
          return (<div><i className={`suppress-panel-styles fa fa-check`} />All Specialties</div>);
        } else {
          return (<>{upp.specialtyCode ? <>{upp.specialtyCode}<br /></> : null}</>);
        }
      })(),
      reason: (() => (
        <>
          {upp.reasonName ? <span title={upp.reasonName}>{upp.reasonName}</span> : "Other"}
          <>
            {upp.allProjects && <div><i className={`suppress-panel-styles fa fa-check`} />All Projects</div>}
          </>
        </>
      ))(),
      action: (() => {
        if (upp.projectionType === ProjectionProviderType.Target) {
          return upp.deleteDate ? "Untargeted" : upp.sourceProjectionProviderSettingsId ? "Update" : "Targeted";
        } else {
          return upp.deleteDate ? "Unexcluded" : "Excluded";
        }
      })(),
      actionBy: upp.deleteUserEmail ?? upp.createUserEmail,
      actionDate: upp.deleteDate ? formatDateTime(upp.deleteDate) : formatDateTime(upp.createDate),
    }
  ));
  /* eslint-enable sort-keys */

  return (
    <AccordionContainer>
      <Accordion
        title={<>{userMdIcon}{`Unprocessed ${projectionProviderType === ProjectionProviderType.Target ? "Targeted" : "Excluded"} Providers`}</>}
        defaultOpen={true}
        testId={`pending-provider-changes__unprocessed-${projectionProviderType}-providers-accordion`}
      >
        <Waffles<PendingWaffleToppings>
          columns={columnConfig}
          data={data}
          disableStickyHeader={true}
          handleCheckboxChange={updateSelectedProviders}
          selectedKeys={selectedProviders}
          showCheckboxes={isEditMode}
          testId={`pending-${projectionProviderType}`}
        />
      </Accordion>
    </AccordionContainer >
  );
};

const renderNetNewProviders = (unprocessedNetNewProviders: NetNewProvider[], selectedProviders: string[], setSelections: React.Dispatch<SetStateAction<string[]>>, user: UserInfo, isEditMode: boolean) => {
  const showNpiColumn = unprocessedNetNewProviders.some((unnp) => !!unnp.npi);
  const showTinColumn = unprocessedNetNewProviders.some((unnp) => !!unnp.tin);

  const updateSelectedProviders = (selectedIds: string[]) => {
    setSelections(selectedIds);
  };

  const columnConfig = getColumnConfiguration(user);
  columnConfig.npi.hidden = !showNpiColumn && showTinColumn;
  columnConfig.tin.hidden = columnConfig.tin.hidden || !showTinColumn;

  /* eslint-disable sort-keys */
  const data: PendingWaffleToppings[] = unprocessedNetNewProviders.map((unnp) => (
    {
      key: unnp.providerId.toString(),
      npi: unnp.npi,
      tin: unnp.tin,
      name: unnp.displayName,
      address: <>
        {unnp.address ? <>{unnp.address}<br /></> : null}
        {unnp.address2 ? <>{unnp.address2}<br /></> : null}
        {unnp.city}
        {unnp.city && (unnp.state || unnp.zip) ? ", " : null}
        {unnp.state} {unnp.zip}
      </>,
      specialtyCode: <>{unnp.specialtyCode}</>,
      reason: (() => (
        <>
          {unnp.reasonName ? <span title={unnp.reasonName}>{unnp.reasonName}</span> : "Other"}
        </>
      ))(),
      action: unnp.isTargeted ? "Targeted" : "Untargeted",
      actionBy: unnp.deleteUserEmail ?? unnp.createUserEmail,
      actionDate: unnp.deleteDate ? formatDateTime(unnp.deleteDate) : formatDateTime(unnp.createDate),
    }
  ));
  /* eslint-enable sort-keys */

  return (
    <AccordionContainer>
      <Accordion
        title={<>{userMdIcon}{`Unprocessed Targeted Net New Providers`}</>}
        defaultOpen={true}
        testId={`pending-provider-changes__unprocessed-net-new-providers-accordion`}
      >
        <Waffles<PendingWaffleToppings>
          columns={columnConfig}
          data={data}
          disableStickyHeader={true}
          handleCheckboxChange={updateSelectedProviders}
          selectedKeys={selectedProviders}
          showCheckboxes={isEditMode}
          testId={`pending-targeted-net-new-providers`}
        />
      </Accordion>
    </AccordionContainer>
  );
};


const renderTerminatedProviders = (unprocessedProviderTerminations: NetworkServicingProviderTerminationModel[], selectedTerminations: string[], setSelectedTerminations: React.Dispatch<SetStateAction<string[]>>, user: UserInfo, isEditMode: boolean) => {
  const showNpiColumn = unprocessedProviderTerminations.some((upt) => !!upt.npi);
  const showTinColumn = unprocessedProviderTerminations.some((upt) => !!upt.tin);

  const columnConfig = getColumnConfiguration(user);
  columnConfig.npi.hidden = !showNpiColumn;
  columnConfig.tin.hidden = columnConfig.tin.hidden || !showTinColumn;

  const updateSelectedTerminations = (selectedKeys: string[]) => {
    setSelectedTerminations(selectedKeys);
  };

  /* eslint-disable sort-keys */
  const data: PendingWaffleToppings[] = unprocessedProviderTerminations.map((upt) => (
    {
      key: (() => {
        if (upt.providerId === 0) {
          return upt.providerTerminationProjectId.toString()
        }
        else {
          return upt.providerId.toString()
        }
      })(),
      npi: upt.npi,
      tin: upt.tin,
      name: upt.displayName,
      address: (() => {
        if (upt.allLocations) {
          return (<div><i className={`suppress-panel-styles fa fa-check`} />All Locations</div>);
        } else {
          return (<>
            {upt.address ? <>{upt.address}<br /></> : null}
            {upt.address2 ? <>{upt.address2}<br /></> : null}
            {upt.city}
            {upt.city && (upt.state || upt.zip) ? ", " : null}
            {upt.state} {upt.zip}
          </>);
        }
      })(),
      specialtyCode: (() => {
        if (upt.allSpecialties) {
          return (<div><i className={`suppress-panel-styles fa fa-check`} />All Specialties</div>);
        } else {
          return (<>{upt.specialtyCode ? <>{upt.specialtyCode}<br /></> : null}</>);
        }
      })(),
      reason: (() => {
        let terminationString = "";
        if (!upt.npi && !upt.tin) {
          terminationString += "Name";
        }
        else if (upt.terminateByNPI) {
          terminationString += "NPI";
          if (upt.terminateByTIN) {
            terminationString += " and TIN";
          }
        } else if (upt.terminateByTIN) {
          terminationString += "TIN";
        }

        return (
          <>
            {<div title={upt.reasonName}>{upt.reasonName}</div>}
            Terminated by {terminationString}
          </>
        );
      })(),
      action: upt.deleteDate ? "Unterminated" : "Terminated",
      actionBy: upt.deleteUserEmail ?? upt.createUserEmail,
      actionDate: upt.deleteDate ? formatDateTime(upt.deleteDate) : formatDateTime(upt.createDate),
    }
  ));
  /* eslint-enable sort-keys */

  return (
    <AccordionContainer>
      <Accordion
        title={<>{userMdIcon}Unprocessed Terminated Providers</>}
        defaultOpen={true}
        testId="pending-provider-changes__unprocessed-terminated-providers-accordion"
      >
        <Waffles<PendingWaffleToppings>
          columns={columnConfig}
          data={data}
          disableStickyHeader={true}
          handleCheckboxChange={updateSelectedTerminations}
          selectedKeys={selectedTerminations}
          showCheckboxes={isEditMode}
          testId="pending-termination"
        />
      </Accordion>
    </AccordionContainer>
  );
};

const renderNoResults = () => {
  return (<QESAlert type="danger">No unprocessed providers found.</QESAlert>);
};

const removeSelectedProjectionProviders = (projectId: number, projectService: ProjectService, selectedProviders: string[], projectedProviders: ProjectionProvider[], refreshKey: number, setRefreshKey: React.Dispatch<SetStateAction<number>>) => {
  if (selectedProviders.length > 0) {
    const deleteIds: number[] = [];
    const restoreIds: number[] = [];
    projectedProviders.forEach((upt) => {
      if (!!upt.deleteDate && selectedProviders.includes(upt.providerId.toString())) {
        restoreIds.push(upt.projectionProviderProjectId);
      } else if (!upt.deleteDate && selectedProviders.includes(upt.providerId.toString())) {
        deleteIds.push(upt.projectionProviderProjectId);
      }
    });
    const promises: Array<Promise<void>> = [];
    if (deleteIds.length) {
      promises.push(projectService.deleteProjectionProviders(projectId, deleteIds));
    }
    if (restoreIds.length) {
      promises.push(projectService.restoreProjectionProviders(projectId, restoreIds));
    }
    Promise.all(promises).then(() => setRefreshKey(refreshKey += 1), () => toast.error("We encountered an issue removing the selected new providers.", { containerId: "z-index-9999" }));
  }
};

const removeSelectedNetNewProviders = (projectId: number, netNewServicingProviderService: NetNewServicingProviderService, selectedProviders: string[], NetNewProviders: NetNewProvider[], refreshKey: number, setRefreshKey: React.Dispatch<SetStateAction<number>>) => {
  if (selectedProviders.length > 0) {
    const providersScheduledForUntargeting: number[] = [];
    const providersScheduledForTargeting: number[] = [];
    NetNewProviders.forEach((upt) => {
      if (!upt.isTargeted && selectedProviders.includes(upt.providerId.toString())) {
        providersScheduledForUntargeting.push(upt.netNewProviderProjectId);
      } else if (upt.isTargeted && selectedProviders.includes(upt.providerId.toString())) {
        providersScheduledForTargeting.push(upt.netNewProviderProjectId);
      }
    });
    const promises: Array<Promise<boolean>> = [];
    if (providersScheduledForUntargeting.length) {
      promises.push(netNewServicingProviderService.updateNetNewIsTargeted(true, providersScheduledForUntargeting));
    }
    if (providersScheduledForTargeting.length) {
      promises.push(netNewServicingProviderService.updateNetNewIsTargeted(false, providersScheduledForTargeting));
    }
    Promise.all(promises).then(() => setRefreshKey(refreshKey += 1), () => toast.error("We encountered an issue targeting or untargeting the selected net new providers.", { containerId: "z-index-9999" }));
  }
};


const removeSelectedTerminationProviders = (projectId: number, terminatedProviders: NetworkServicingProviderTerminationModel[], providerTerminationService: ProviderTerminationService, selectedTerminations: string[], refreshKey: number, setRefreshKey: React.Dispatch<SetStateAction<number>>) => {
  if (selectedTerminations.length > 0) {
    const deleteIds: number[] = [];
    const restoreIds: number[] = [];
    terminatedProviders.forEach((upt) => {
      if (!!upt.deleteDate && (selectedTerminations.includes(upt.providerTerminationProjectId.toString()) || selectedTerminations.includes(upt.providerId.toString()))) {
        restoreIds.push(upt.providerTerminationProjectId);
      } else if (!upt.deleteDate && (selectedTerminations.includes(upt.providerTerminationProjectId.toString()) || selectedTerminations.includes(upt.providerId.toString()))) {
        deleteIds.push(upt.providerTerminationProjectId);
      }
    });
    const promises: Array<Promise<void>> = [];
    if (deleteIds.length) {
      promises.push(providerTerminationService.deleteTermination(projectId, deleteIds));
    }
    if (restoreIds.length) {
      promises.push(providerTerminationService.restoreTermination(projectId, restoreIds));
    }
    Promise.all(promises).then(() => setRefreshKey(refreshKey += 1), () => toast.error("We encountered an issue removing the selected new termination providers.", { containerId: "z-index-9999" }));
  }
};

export { PendingProviderChanges };
